// src/render/contentLines.ts
/**
 * Render multiline section text into DOM with animation metadata for GSAP.
 *
 * - Turns lines into <h1>, <h2>, <p>, and in-line token spans.
 * - Writes only data attributes: data-anim, data-anim-delay.
 * - NO CSS-driven transitions/animations. GSAP owns playback.
 * - Keeps overlay-box wrapping and divider sizing logic.
 */



import { applyFormatting } from "../utils/formatting";
import { parseInlineAttrs } from "../markdown/parseInlineAttrs";
// getTransform is no longer needed for CSS classes; keep only if you want a fallback class.
// import { getTransform } from "../utils/transform";

// --- Auto-link plain URLs, emails, and phone numbers in inline HTML ---
function linkifyInline(input: string): string {
  if (!input) return input;

  // Split on existing anchors so we don't double-wrap their contents
  const ANCHOR_SPLIT = /(<a\b[^>]*>.*?<\/a>)/gis;

  const parts = input.split(ANCHOR_SPLIT);
  for (let i = 0; i < parts.length; i++) {
    // Odd indices are existing anchors -> keep as-is
    if (i % 2 === 1) continue;
    parts[i] = linkifySegment(parts[i]);
  }
  return parts.join("");
}

function linkifySegment(text: string): string {
  if (!text) return text;

  // 1) URLs (http/https/www.)
  const urlRe = /\b((?:https?:\/\/|www\.)[^\s<]+)/gi;
  text = text.replace(urlRe, (match: string, url: string) => {
    let href = url;
    if (/^www\./i.test(url)) href = "https://" + url;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });

  // 2) Emails
  const emailRe = /\b([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})\b/gi;
  text = text.replace(emailRe, (match: string, email: string) => {
    return `<a href="mailto:${email}" target="_blank" rel="noopener noreferrer">${email}</a>`;
  });

  // 3) Phone numbers (basic North America forms)
  // Matches: 403-892-6860, (403) 892-6860, 403.892.6860, +1 403 892 6860, etc.
  const phoneRe = /(?<!\w)(?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)\d{3}[-.\s]?\d{4}(?!\w)/g;
  text = text.replace(phoneRe, (m: string) => {
    const digits = m.replace(/\D/g, "");
    // require at least 10 digits to avoid linking short numbers
    if (digits.length < 10) return m;
    let tel = digits;
    if (digits.length === 10) tel = "1" + digits;
    if (!tel.startsWith("+")) tel = "+" + tel;
    return `<a href="tel:${tel}" target="_blank" rel="noopener noreferrer">${m}</a>`;
  });

  return text;
}

type FramePos = "left" | "center" | "right";
const isFramePos = (x: any): x is FramePos =>
  x === "left" || x === "center" || x === "right";

const speedFrom = (v: any): number => {
  if (v == null) return 1;
  const t = String(v).trim().toLowerCase();
  if (!t) return 1;
  if (t === 'slow') return 1.6;   // ~60% slower
  if (t === 'fast') return 0.75;  // ~25% faster
  const n = parseFloat(t);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const normalizeAnim = (key?: string): string => {
  if (!key) return "float-up";
  const k = key.toLowerCase().trim();
  if (k === "cinematic") return "float-up-cinematic";
  return k;
};

// Respect reduced motion dynamically
const prefersReduced = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function createAnimSpan(
  html: string,
  anim: string,
  delay: number
): HTMLSpanElement {
  const span = document.createElement("span");
  span.className = "inline-block sc-anim"; // keep sc-anim as a neutral hook
  span.innerHTML = html;
  span.dataset.anim = normalizeAnim(anim);
  span.dataset.animDelay = String(delay);
  // Optional: helps avoid FOUC before GSAP sets states
  span.style.opacity = "0";
  span.style.willChange = "transform, opacity, filter";
  return span;
}

function createAnimatedEl(
  tag: keyof HTMLElementTagNameMap,
  content: string,
  baseClass: string,
  animType: string,
  lineStartDelay: number,       // absolute start for this line
  speedFactor: number,          // 1 = normal, <1 faster, >1 slower
  enableInline: boolean,
  absoluteInline = true
): HTMLElement {
  const el = document.createElement(tag);
  const chosenAnim = normalizeAnim(animType);
  const reduced = prefersReduced();
  const hasInlineTokens = enableInline && content.includes("^^");

  // Parent animation: only when there are no inline tokens on this line.
  // If inline tokens exist, keep the parent static to avoid compounding vectors
  // (e.g., parent float-up + child slide-right = diagonal).
  if (hasInlineTokens) {
    el.className = baseClass;
    el.style.willChange = "transform, opacity, filter";
  } else {
    el.className = `${baseClass} sc-anim`;
    el.style.opacity = "0";
    el.style.willChange = "transform, opacity, filter";
    el.dataset.anim = chosenAnim;

    const effectiveDelay = reduced ? Math.min(lineStartDelay, 0.3) : lineStartDelay;
    el.dataset.animDelay = String(effectiveDelay);
  }

  if (!hasInlineTokens) {
    el.innerHTML = linkifyInline(applyFormatting(content));
    return el;
  }

  // Inline tokens: ^^text^^(time[, anim])
const tokenRe = /\^\^([\s\S]+?)\^\^\(([^)]*)\)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  const pushPlain = (txt: string) => {
    if (!txt) return;
    if (hasInlineTokens) {
      // With inline tokens on this line, keep surrounding text static
      const span = document.createElement("span");
      span.innerHTML = linkifyInline(applyFormatting(txt));
      el.appendChild(span);
    } else {
      const base = reduced ? Math.min(lineStartDelay, 0.3) : lineStartDelay;
      const d = base + ((reduced ? 0.2 : 0.3) * speedFactor);
      const span = createAnimSpan(linkifyInline(applyFormatting(txt)), chosenAnim, d);
      el.appendChild(span);
    }
  };

  while ((m = tokenRe.exec(content)) !== null) {
    const [full, rawText, specBody] = m;
    const before = content.slice(lastIndex, m.index);
    if (before) pushPlain(before);

    const { delay: parsedDelay, animName: inlineAnim, attrs: inlineAttrs } = parseInlineSpec(specBody || "");
    const chosenInline = normalizeAnim(inlineAnim || animType);
    const abs = absoluteInline ? parsedDelay : (lineStartDelay + parsedDelay);
    const effInline = reduced ? Math.min(abs, 0.3) : abs;

    const padded = rawText.endsWith(" ") ? rawText : rawText + " ";
    const token = createAnimSpan(linkifyInline(applyFormatting(padded)), chosenInline, effInline);
    if (inlineAttrs && typeof inlineAttrs === "object") {
      Object.entries(inlineAttrs).forEach(([rawKey, rawVal]) => {
        const key = rawKey.toLowerCase().trim();
        if (key === "text-panel" || key === "panel" || key === "textpanel") {
          const panel = String(rawVal || "").toLowerCase().trim();
          if (panel) {
            token.dataset.textPanel = panel;
            token.classList.add("sc-text-panel");
          }
          return;
        }
        if (key === "text-style" || key === "textstyle" || key === "style") {
          const styleVal = String(rawVal || "").toLowerCase().trim();
          if (styleVal) token.dataset.textStyle = styleVal;
          return;
        }
        const camel = toDatasetKey(key);
        (token.dataset as any)[camel] = String(rawVal);
      });
    }
    el.appendChild(token);

    lastIndex = m.index + full.length;
  }

  if (lastIndex < content.length) pushPlain(content.slice(lastIndex));
  return el;
}

function parseInlineSpec(spec: string): { delay: number; animName?: string; attrs?: Record<string, any> } {
  const tokens = spec.split(",").map((part) => part.trim()).filter(Boolean);
  const first = tokens.shift() || "0";
  const delay = parseFloat(first) || 0;

  let animName: string | undefined;
  const attrTokens: string[] = [];

  tokens.forEach((tok) => {
    if (tok.includes(":")) {
      attrTokens.push(tok);
    } else if (!animName) {
      animName = tok;
    }
  });

  const attrs = attrTokens.length ? parseInlineAttrs(attrTokens.join(", ")) : undefined;
  return { delay, animName, attrs };
}

export function renderSectionContentLines(
  container: HTMLElement,
  attrs: Record<string, any>,
  content: string
): void {
  // --- Normalize image header strings before rendering ---
  if (typeof attrs.image === "string") {
    const raw = attrs.image.trim();
    // Match:  "filename.ext { key: value, key2: value2 }"  OR just  "filename.ext"
    const m = raw.match(/^"?([^"{]+?)"?\s*(\{[^}]*\})?\s*$/);
    if (m) {
      const image = m[1].trim();
      const obj: Record<string, any> = { image };
      if (m[2]) {
        // Parse simple "{ key: value, key2: value2 }" into an object without eval
        const body = m[2].slice(1, -1); // strip braces
        // Split on commas not inside quotes (attributes are simple; no nested structures expected)
        const parts = body.split(/,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)/).map(s => s.trim()).filter(Boolean);
        for (const part of parts) {
          const kv = part.split(/:(.+)/); // split on first ':'
          if (kv.length >= 2) {
            const key = kv[0].trim().replace(/^"|"$/g, "");
            let val = kv[1].trim();
            // Remove surrounding quotes if present
            if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
              val = val.slice(1, -1);
            }
            // Coerce numbers and booleans; leave others as strings
            if (/^\d+(?:\.\d+)?(?:ms|s)?$/i.test(val)) {
              obj[key] = val; // keep timing tokens like "3000ms" or "1.2s" as-is
            } else if (/^\d+(?:\.\d+)?$/.test(val)) {
              obj[key] = parseFloat(val);
            } else if (/^(true|false)$/i.test(val)) {
              obj[key] = /true/i.test(val);
            } else {
              obj[key] = val;
            }
          }
        }
      }
      attrs.image = obj;
    }
  }

  const speed = speedFrom(attrs["anim-speed"] ?? attrs["animation-speed"]);

  let totalHeight = 0;
  const vh = (typeof window !== "undefined" ? window.innerHeight : 1000);
  const maxHeight = vh * 0.9;

  const lines = content.split("\n");
  const reduced = prefersReduced();
  let currentDelay = (reduced ? 0.2 : 0.6) * speed;

  // Content-length aware stagger (author can still override with absolute "(t)")
  const len = Math.max(0, content.length);
  const baseStagger = Math.max(0.20, 0.55 - Math.min(0.35, len / 1200));
  const staggerBase = reduced ? Math.min(0.20, baseStagger) : baseStagger;
  const stagger = staggerBase * speed;

  let useOverlay = attrs["overlay-box"] === true || attrs["overlay-box"] === "true";
  let inlineAlign: string | null = typeof attrs["align"] === "string"
    ? String(attrs["align"]).toLowerCase()
    : null;
  let inlineFrame: FramePos | null = null;

  let lastTextEl: HTMLElement | null = null;

  const parseLineAbsoluteTime = (s: string): { text: string; time: number | null } => {
    const m = s.match(/\s*\((\d+(?:\.\d+)?)s?\)\s*$/);
    if (!m) return { text: s, time: null };
    const t = parseFloat(m[1]);
    return { text: s.slice(0, m.index).trim(), time: isNaN(t) ? null : t };
  };

  for (let rawLine of lines) {
    let line = rawLine.trim();
    if (!line || line.startsWith("<!--")) continue;

    const absRes = parseLineAbsoluteTime(line);
    line = absRes.text;
    const lineHasAbsolute = absRes.time !== null;
    const lineStart = lineHasAbsolute ? (absRes.time as number) : currentDelay;

    if (line.startsWith("::overlay:")) {
      const val = line.split("::overlay:")[1]?.trim().toLowerCase();
      useOverlay = val === "on" || val === "true";
      if (val === "off" || val === "false") useOverlay = false;
      continue;
    }

    if (line.startsWith("::delay:")) {
      const raw = line.split("::delay:")[1]?.trim();
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) currentDelay = parsed;
      continue;
    }

    if (line.startsWith("::space:")) {
      const size = line.split("::space:")[1]?.trim() || "2rem";
      const spacer = document.createElement("div");
      spacer.style.height = size;
      container.appendChild(spacer);

      // estimate height
      let heightPx = 32;
      if (size.endsWith("rem")) {
        const v = parseFloat(size);
        if (!isNaN(v)) heightPx = v * 16;
      } else if (size.endsWith("px")) {
        const v = parseFloat(size);
        if (!isNaN(v)) heightPx = v;
      }
      totalHeight += heightPx;
      continue;
    }

    if (line.startsWith("::align:")) {
      const val = line.split("::align:")[1]?.trim().toLowerCase();
      let a = val;
      if (a === "middle") a = "center";
      if (["left","center","right"].includes(a)) inlineAlign = a;
      continue;
    }

    if (line.startsWith("::frame:")) {
      const val = line.split("::frame:")[1]?.trim().toLowerCase();
      const f = val === "middle" ? "center" : val;
      // allow clearing with ::frame: none/off/clear/reset
      if (f && (f === "none" || f === "off" || f === "clear" || f === "reset")) {
        inlineFrame = null;
      } else if (isFramePos(f)) {
        inlineFrame = f;
      }
      continue;
    }

    // --- Dividers ---
    if (/^\s*\*{3}\s*$/.test(line) || /^\s*\*{5,}\s*$/.test(line)) {
      const long = /^\s*\*{5,}\s*$/.test(line);
      const hr = document.createElement("hr");
      hr.className = `sc-divider ${long ? "sc-divider-long" : "sc-divider-short"} sc-anim`;
      hr.style.opacity = "0";
      hr.style.willChange = "transform, opacity, filter";
      const anim = (attrs["divider-anim"] as string) || "grow-x";
      hr.dataset.anim = normalizeAnim(anim);
      hr.dataset.animDelay = String(lineStart);

      try {
        if (!long) {
          const target = lastTextEl || (container.lastElementChild as HTMLElement | null);
          if (target) {
            const cs = getComputedStyle(target);
            const align = (cs.textAlign || "left").toLowerCase();
            const cRect = container.getBoundingClientRect();
            const tRect = target.getBoundingClientRect();
            const widthPx = Math.max(24, Math.min(tRect.width, cRect.width * 0.9));
            hr.style.width = `${Math.round(widthPx)}px`;
            if (align === "center") {
              hr.style.marginLeft = "auto";
              hr.style.marginRight = "auto";
            } else if (align === "right") {
              hr.style.marginLeft = "auto";
              hr.style.marginRight = "0";
            } else {
              const left = Math.max(0, tRect.left - cRect.left);
              hr.style.marginLeft = `${Math.round(left)}px`;
              hr.style.marginRight = "auto";
            }
          }
        }
      } catch {}

      if (useOverlay) {
        const box = document.createElement("div");
        box.className = "overlay-box sc-anim";
        box.style.opacity = "0";
        box.dataset.anim = "fade-in";
        box.dataset.animDelay = String(lineStart + (0.5 * speed));
        box.appendChild(hr);
        container.appendChild(box);
      } else {
        container.appendChild(hr);
      }

      totalHeight += 16;
      currentDelay = lineHasAbsolute ? Math.max(currentDelay, lineStart) + 0.25 : currentDelay + 0.25;
      continue;
    }

    // Headings
    const tag = line.startsWith("#!") ? "h1"
              : line.startsWith("##!") ? "h2"
              : line.startsWith("##") ? "h2"
              : null;
    const contentText = tag ? line.replace(/^#+!*/, "").trim() : line;

    if (tag) {
      if (totalHeight > maxHeight) {
        console.warn("Skipping content due to height limit:", line);
        continue;
      }

      const classMap = {
        h1: "text-5xl md:text-6xl font-bold",
        h2: line.startsWith("##!") ? "text-3xl md:text-4xl font-semibold" : "text-2xl md:text-3xl font-semibold"
      } as const;

      const animKey = line.startsWith("#!") ? "title-anim"
                    : line.startsWith("##!") ? "subtitle-anim"
                    : "header-anim";
      const anim = normalizeAnim(attrs[animKey] || "float-up");

      const el = createAnimatedEl(tag, contentText, classMap[tag], anim, lineStart, speed, true, true);
      if (inlineAlign) {
        el.style.textAlign = inlineAlign;
        inlineAlign = null;
      }

      // --- append with optional ::frame: wrapper (landscape only) ---
      const isPortrait = typeof window !== "undefined" && !!(window.matchMedia?.("(orientation: portrait)")?.matches);
      const maybeWrapFrame = (node: HTMLElement) => {
        if (!isPortrait && inlineFrame) {
          // mark container so CSS can grid only when frames exist
          (container as HTMLElement).setAttribute("data-has-frames", "1");
          const wrap = document.createElement("div");
          wrap.className = `frame frame--${inlineFrame}`;
          wrap.appendChild(node);
          // consume the one-shot directive
          inlineFrame = null;
          return wrap;
        }
        return node;
      };

      if (useOverlay) {
        const box = document.createElement("div");
        box.className = "overlay-box sc-anim";
        box.style.opacity = "0";
        box.dataset.anim = "fade-in";
        box.dataset.animDelay = String(lineStart + (0.5 * speed));
        box.appendChild(el);
        const target = maybeWrapFrame(box);
        container.appendChild(target);
      } else {
        const target = maybeWrapFrame(el);
        container.appendChild(target);
      }

      lastTextEl = el;
      if (tag === "h1") totalHeight += 80;
      else totalHeight += 60;

      const isCinematic = anim === "cinematic" || anim === "float-up-cinematic";
      const step = isCinematic
        ? (prefersReduced() ? 0.25 : Math.min(0.6, Math.max(0.4, stagger + 0.2)))
        : (prefersReduced() ? Math.min(0.2, stagger) : stagger);

      currentDelay = lineHasAbsolute
        ? Math.max(currentDelay, lineStart) + step
        : currentDelay + step;

      continue;
    }

    // Paragraphs
    if (totalHeight > maxHeight) {
      console.warn("Skipping content due to height limit:", line);
      continue;
    }

    const paraAnim = normalizeAnim(attrs["paragraph-anim"] || "fade-in");
    const p = createAnimatedEl("p", contentText, "text-lg md:text-xl mt-3", paraAnim, lineStart, speed, true, true);
    if (inlineAlign) {
      p.style.textAlign = inlineAlign;
      inlineAlign = null;
    }

    // --- append with optional ::frame: wrapper (landscape only) ---
    const isPortrait = typeof window !== "undefined" && !!(window.matchMedia?.("(orientation: portrait)")?.matches);
    const maybeWrapFrame = (node: HTMLElement) => {
      if (!isPortrait && inlineFrame) {
        (container as HTMLElement).setAttribute("data-has-frames", "1");
        const wrap = document.createElement("div");
        wrap.className = `frame frame--${inlineFrame}`;
        wrap.appendChild(node);
        inlineFrame = null; // consume one-shot
        return wrap;
      }
      return node;
    };

    if (useOverlay) {
      const box = document.createElement("div");
      box.className = "overlay-box sc-anim";
      box.style.opacity = "0";
      box.dataset.anim = "fade-in";
      box.dataset.animDelay = String(lineStart + (0.5 * speed));
      box.appendChild(p);
      const target = maybeWrapFrame(box);
      container.appendChild(target);
    } else {
      const target = maybeWrapFrame(p);
      container.appendChild(target);
    }

    lastTextEl = p;
    const isCinematicPara = paraAnim === "cinematic" || paraAnim === "float-up-cinematic";
    const stepPara = isCinematicPara
      ? (prefersReduced() ? 0.25 : Math.min(0.6, Math.max(0.4, stagger + 0.2)))
      : (prefersReduced() ? Math.min(0.2, stagger) : stagger);
    currentDelay = lineHasAbsolute
      ? Math.max(currentDelay, lineStart) + stepPara
      : currentDelay + stepPara;

    totalHeight += 40;
  }

  // Gentle autoscale if overflowing
  const autoscale = attrs["autoscale"];
  if (autoscale !== "off" && autoscale !== false) {
    if (totalHeight > maxHeight) {
      const scale = Math.min(1, Math.max(0.96, maxHeight / totalHeight));
      container.style.transform = `scale(${scale})`;
      container.style.transformOrigin = "top";
    }
  }
}
const toDatasetKey = (key: string) =>
  key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
